<template>
  <div class="relative flex-1">
    <FormField
      ref="formField"
      :value="queryText"
      :title="$t('search-form.departure.desktop.title')"
      :placeholder="$t('search-form.departure.desktop.placeholder')"
      :is-active="isActive"
      rounded-left
      @onInput="onFieldInput"
      @onClick="emit('onFieldSelect')"
      @onFocus="emit('onFieldSelect')"
    />

    <TownFromStep
      v-if="isActive"
      ref="dropdown"
      v-on-click-outside="onClickOutsideHandler"
      :selected-town="selectedTownFrom"
      :query="queryText"
      :is-filter-by-query-enabled="isFilterByQueryEnabled"
      class="absolute z-30 mt-2 w-full"
      @onSelect="onTownFromSelect"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import { Tourflow } from '@aviata/platform-types';

  import type { OnOutsideClickHandler } from '@shared/types';

  import { SearchFormField } from '../../../types';
  import FormField from '../FormField.vue';
  import TownFromStep from './TownFromStep.vue';

  const props = defineProps<{
    isActive: boolean;
    selectedTownFrom?: Tourflow.TownFrom;
    activeFormField?: SearchFormField;
  }>();

  const emit = defineEmits<{
    onFieldSelect: [];
    onClickOutsideField: [];
    onTownFromSelect: [town: Tourflow.TownFrom];
  }>();

  const dropdown = ref<HTMLElement>();
  const formField = ref<HTMLElement>();
  const queryText = ref(props.selectedTownFrom?.name ?? '');
  const isFilterByQueryEnabled = ref(false);

  watch(
    () => props.activeFormField,
    field => {
      if (field === SearchFormField.TOWN_TO_FORM_FIELD) {
        formField.value?.focus();
      }
    },
  );

  watch(
    () => props.selectedTownFrom,
    town => {
      if (town) {
        queryText.value = town.name;
      }
    },
  );

  function onTownFromSelect(town: Tourflow.TownFrom) {
    queryText.value = town.name;
    isFilterByQueryEnabled.value = false;
    emit('onTownFromSelect', town);
  }

  function resetQueryText() {
    queryText.value = props.selectedTownFrom?.name || '';
  }

  function onFieldInput(event: Event) {
    const target = event.target as HTMLInputElement;
    queryText.value = target.value;
    isFilterByQueryEnabled.value = true;
  }

  const onClickOutsideHandler: OnOutsideClickHandler = [
    () => {
      resetQueryText();
      emit('onClickOutsideField');
    },
    { ignore: [formField] },
  ];
</script>
